import React, { useState, useEffect } from 'react';
import type { CandidateStage } from '../../services/unified/types';
import { useGenerateCandidateEmail } from '../../hooks/unified/useAIQueries';
import { useSendCandidateEmail } from '../../hooks/unified/useCandidatesQueries';
import { RichTextEditor } from '../ui/RichTextEditor';
import { LoadingSpinner } from '../ui/LoadingSpinner';

interface EmailComposerModalProps {
  isOpen: boolean;
  onClose: () => void;
  jobId: string;
  candidateId: string;
  candidateName: string;
  jobTitle: string;
  companyName: string;
  currentStage: CandidateStage;
  emailHistory?: EmailHistoryEntry[];
}

interface EmailHistoryEntry {
  id: string;
  subject: string;
  content: string;
  sent_at: string;
  sent_by?: string;
}

export const EmailComposerModal: React.FC<EmailComposerModalProps> = ({
  isOpen,
  onClose,
  jobId,
  candidateId,
  candidateName,
  jobTitle,
  companyName,
  currentStage,
  emailHistory = [],
}) => {
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [ccEmails, setCcEmails] = useState<string[]>([]);
  const [ccInput, setCcInput] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const generateEmailMutation = useGenerateCandidateEmail();
  const sendEmailMutation = useSendCandidateEmail();

  // Generate email content when modal opens or stage changes
  useEffect(() => {
    if (isOpen && !content) {
      handleGenerateEmail();
    }
  }, [isOpen, currentStage]);

  const handleGenerateEmail = async () => {
    try {
      const result = await generateEmailMutation.mutateAsync({
        candidate_name: candidateName,
        job_title: jobTitle,
        company_name: companyName,
        stage: currentStage,
      });

      if (typeof result.content === 'string') {
        // Parse the generated content to extract subject and body
        const lines = result.content.split('\n');
        const subjectLine = lines.find(line => line.toLowerCase().startsWith('subject:'));
        
        if (subjectLine) {
          setSubject(subjectLine.replace(/^subject:\s*/i, '').trim());
          const bodyStartIndex = lines.indexOf(subjectLine) + 1;
          setContent(lines.slice(bodyStartIndex).join('\n').trim());
        } else {
          setContent(result.content);
          setSubject(getDefaultSubject(currentStage));
        }
      }
    } catch (error) {
      console.error('Failed to generate email:', error);
      // Set default content on error
      setSubject(getDefaultSubject(currentStage));
      setContent(getDefaultContent(currentStage, candidateName, jobTitle, companyName));
    }
  };

  const getDefaultSubject = (stage: CandidateStage): string => {
    const subjects: Record<CandidateStage, string> = {
      'Applied': `Application Received - ${jobTitle}`,
      'Resume Review': `Application Update - ${jobTitle}`,
      'Shortlisted': `Congratulations! You've Been Shortlisted - ${jobTitle}`,
      'Interview Scheduled': `Interview Invitation - ${jobTitle} at ${companyName}`,
      'Interview Completed': `Thank You for Interviewing - ${jobTitle}`,
      'Offer Extended': `Job Offer - ${jobTitle} at ${companyName}`,
      'Hired': `Welcome to ${companyName}!`,
      'Rejected': `Application Status Update - ${jobTitle}`,
    };
    return subjects[stage] || `Update on Your Application - ${jobTitle}`;
  };

  const getDefaultContent = (
    stage: CandidateStage,
    name: string,
    job: string,
    company: string
  ): string => {
    const templates: Record<CandidateStage, string> = {
      'Applied': `<p>Dear ${name},</p><p>Thank you for applying for the ${job} position at ${company}. We have received your application and our team will review it shortly.</p><p>We appreciate your interest in joining our team.</p><p>Best regards,<br>${company} Hiring Team</p>`,
      'Resume Review': `<p>Dear ${name},</p><p>We are currently reviewing your application for the ${job} position. We will be in touch soon with next steps.</p><p>Best regards,<br>${company} Hiring Team</p>`,
      'Shortlisted': `<p>Dear ${name},</p><p>Congratulations! We are pleased to inform you that you have been shortlisted for the ${job} position at ${company}.</p><p>We will contact you soon to schedule the next steps in our hiring process.</p><p>Best regards,<br>${company} Hiring Team</p>`,
      'Interview Scheduled': `<p>Dear ${name},</p><p>We are excited to invite you for an interview for the ${job} position at ${company}.</p><p>Interview details will be shared separately.</p><p>We look forward to meeting you!</p><p>Best regards,<br>${company} Hiring Team</p>`,
      'Interview Completed': `<p>Dear ${name},</p><p>Thank you for taking the time to interview for the ${job} position at ${company}. We enjoyed learning more about your experience and qualifications.</p><p>We will be in touch soon with our decision.</p><p>Best regards,<br>${company} Hiring Team</p>`,
      'Offer Extended': `<p>Dear ${name},</p><p>We are delighted to extend an offer for the ${job} position at ${company}!</p><p>Please find the detailed offer letter attached. We look forward to welcoming you to our team.</p><p>Best regards,<br>${company} Hiring Team</p>`,
      'Hired': `<p>Dear ${name},</p><p>Welcome to ${company}! We are thrilled to have you join our team as ${job}.</p><p>We will be in touch with onboarding details shortly.</p><p>Best regards,<br>${company} Team</p>`,
      'Rejected': `<p>Dear ${name},</p><p>Thank you for your interest in the ${job} position at ${company}. After careful consideration, we have decided to move forward with other candidates.</p><p>We appreciate the time you invested in the application process and wish you the best in your job search.</p><p>Best regards,<br>${company} Hiring Team</p>`,
    };
    return templates[stage] || `<p>Dear ${name},</p><p>Thank you for your application.</p><p>Best regards,<br>${company} Team</p>`;
  };

  const handleAddCc = () => {
    const email = ccInput.trim();
    if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && !ccEmails.includes(email)) {
      setCcEmails([...ccEmails, email]);
      setCcInput('');
    }
  };

  const handleRemoveCc = (email: string) => {
    setCcEmails(ccEmails.filter(e => e !== email));
  };

  const handleSendEmail = async () => {
    if (!subject.trim() || !content.trim()) {
      alert('Please provide both subject and content');
      return;
    }

    try {
      await sendEmailMutation.mutateAsync({
        jobId,
        candidateId,
        emailData: {
          subject,
          content,
          cc: ccEmails.length > 0 ? ccEmails : undefined,
        },
      });

      // Close modal and reset state
      onClose();
      setSubject('');
      setContent('');
      setCcEmails([]);
      setShowConfirmation(false);
    } catch (error) {
      console.error('Failed to send email:', error);
      alert('Failed to send email. Please try again.');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-semibold text-gray-900">Compose Email</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => {
              setShowPreview(false);
              setShowHistory(false);
            }}
            className={`px-6 py-3 font-medium ${
              !showPreview && !showHistory
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Compose
          </button>
          <button
            onClick={() => {
              setShowPreview(true);
              setShowHistory(false);
            }}
            className={`px-6 py-3 font-medium ${
              showPreview
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Preview
          </button>
          <button
            onClick={() => {
              setShowHistory(true);
              setShowPreview(false);
            }}
            className={`px-6 py-3 font-medium ${
              showHistory
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            History ({emailHistory.length})
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {!showPreview && !showHistory && (
            <div className="space-y-4">
              {/* Recipient Info */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-gray-700">
                  <span className="font-medium">To:</span> {candidateName}
                </p>
                <p className="text-sm text-gray-700">
                  <span className="font-medium">Position:</span> {jobTitle}
                </p>
                <p className="text-sm text-gray-700">
                  <span className="font-medium">Stage:</span> {currentStage}
                </p>
              </div>

              {/* Generate Button */}
              <button
                onClick={handleGenerateEmail}
                disabled={generateEmailMutation.isPending}
                className="w-full bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
              >
                {generateEmailMutation.isPending ? (
                  <>
                    <LoadingSpinner size="sm" className="mr-2" />
                    Generating AI Content...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                    </svg>
                    Regenerate with AI
                  </>
                )}
              </button>

              {/* Subject */}
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                  Subject *
                </label>
                <input
                  id="subject"
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Email subject..."
                  required
                />
              </div>

              {/* CC Emails */}
              <div>
                <label htmlFor="cc" className="block text-sm font-medium text-gray-700 mb-2">
                  CC (Optional)
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    id="cc"
                    type="email"
                    value={ccInput}
                    onChange={(e) => setCcInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCc())}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Add CC email address..."
                  />
                  <button
                    onClick={handleAddCc}
                    className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Add
                  </button>
                </div>
                {ccEmails.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {ccEmails.map((email) => (
                      <span
                        key={email}
                        className="inline-flex items-center bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm"
                      >
                        {email}
                        <button
                          onClick={() => handleRemoveCc(email)}
                          className="ml-2 text-blue-600 hover:text-blue-800"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Content Editor */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Content *
                </label>
                <RichTextEditor
                  value={content}
                  onChange={setContent}
                  placeholder="Write your email content here..."
                  minHeight="300px"
                />
              </div>
            </div>
          )}

          {showPreview && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-6 rounded-lg">
                <div className="mb-4 pb-4 border-b border-gray-200">
                  <p className="text-sm text-gray-600 mb-1">
                    <span className="font-medium">To:</span> {candidateName}
                  </p>
                  <p className="text-sm text-gray-600 mb-1">
                    <span className="font-medium">Subject:</span> {subject || '(No subject)'}
                  </p>
                  {ccEmails.length > 0 && (
                    <p className="text-sm text-gray-600">
                      <span className="font-medium">CC:</span> {ccEmails.join(', ')}
                    </p>
                  )}
                </div>
                <div
                  className="prose max-w-none"
                  dangerouslySetInnerHTML={{ __html: content || '<p class="text-gray-400">(No content)</p>' }}
                />
              </div>
            </div>
          )}

          {showHistory && (
            <div className="space-y-4">
              {emailHistory.length > 0 ? (
                emailHistory.map((entry) => (
                  <div key={entry.id} className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-gray-900">{entry.subject}</h4>
                      <span className="text-sm text-gray-500">{formatDate(entry.sent_at)}</span>
                    </div>
                    {entry.sent_by && (
                      <p className="text-sm text-gray-600 mb-2">Sent by: {entry.sent_by}</p>
                    )}
                    <div
                      className="prose prose-sm max-w-none text-gray-700"
                      dangerouslySetInnerHTML={{ __html: entry.content }}
                    />
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <svg className="w-12 h-12 mx-auto mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <p>No email history available</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {!showHistory && (
          <div className="flex items-center justify-between p-6 border-t border-gray-200">
            <button
              onClick={onClose}
              className="bg-gray-200 text-gray-800 px-6 py-2 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => setShowConfirmation(true)}
              disabled={sendEmailMutation.isPending || !subject.trim() || !content.trim()}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center"
            >
              {sendEmailMutation.isPending ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  Sending...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
                  </svg>
                  Send Email
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Confirm Send Email</h3>
            <p className="text-gray-700 mb-6">
              Are you sure you want to send this email to {candidateName}?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirmation(false)}
                className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSendEmail}
                disabled={sendEmailMutation.isPending}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                {sendEmailMutation.isPending ? 'Sending...' : 'Confirm Send'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
